<template>
    <v-card>
        <v-card-title>{{ $t("welcome") }}</v-card-title>
    </v-card>

    <v-container fluid>
        <dashboard-stats-row>
            <template #icon>
                <img :src="truckPng" alt="Truck" width="100" />
            </template>
            <v-col cols="12" sm="6" lg="6" xl="2" class="d-flex flex-column">
                <dashboard-summary-card
                    :title="$t('dashboard.totalTrucks')"
                    :value="truckCount"
                    :color="'green'"
                    @click="$router.push('/dashboard/search-trucks')"
                >
                    <template #subtitle>
                        <v-icon size="18" class="mr-1">mdi-eye</v-icon>
                        {{ truckViews }} {{ $t("dashboard.views") }}
                    </template>
                </dashboard-summary-card>
            </v-col>
            <v-col cols="12" sm="6" lg="6" xl="2" class="d-flex flex-column">
                <dashboard-reservation-card
                    :title="$t('dashboard.totalTruckReservations')"
                    :received="truckReceived"
                    :contracted="truckContracted"
                    entry-type="truck"
                />
            </v-col>
            <v-col cols="12" sm="6" lg="6" xl="2" class="d-flex flex-column">
                <dashboard-summary-card
                    :title="$t('dashboard.expires6hours')"
                    :value="truckExpiring6h"
                    color="red"
                    @click="$router.push({ path: '/dashboard/search-trucks', query: { expiresIn: '6h' } })"
                >
                    <template #subtitle>{{ $t("dashboard.entries") }}</template>
                </dashboard-summary-card>
            </v-col>
            <v-col cols="12" sm="6" lg="6" xl="2" class="d-flex flex-column">
                <dashboard-summary-card
                    :title="$t('dashboard.expires1day')"
                    :value="truckExpiring1d"
                    color="red"
                    @click="$router.push({ path: '/dashboard/search-trucks', query: { expiresIn: '1d' } })"
                >
                    <template #subtitle>{{ $t("dashboard.entries") }}</template>
                </dashboard-summary-card>
            </v-col>
        </dashboard-stats-row>

        <dashboard-stats-row>
            <template #icon>
                <img :src="warePng" alt="Ware" width="100" />
            </template>
            <v-col cols="12" sm="6" lg="6" xl="2" class="d-flex flex-column">
                <dashboard-summary-card
                    :title="$t('dashboard.totalWares')"
                    :value="wareCount"
                    :color="'green'"
                    @click="$router.push('/dashboard/search-wares')"
                >
                    <template #subtitle>
                        <v-icon size="18" class="mr-1">mdi-eye</v-icon>
                        {{ wareViews }} {{ $t("dashboard.views") }}
                    </template>
                </dashboard-summary-card>
            </v-col>
            <v-col cols="12" sm="6" lg="6" xl="2" class="d-flex flex-column">
                <dashboard-reservation-card
                    :title="$t('dashboard.totalWareReservations')"
                    :received="wareReceived"
                    :contracted="wareContracted"
                    entry-type="ware"
                />
            </v-col>
            <v-col cols="12" sm="6" lg="6" xl="2" class="d-flex flex-column">
                <dashboard-summary-card
                    :title="$t('dashboard.expires4hours')"
                    :value="wareExpiring4h"
                    color="red"
                    @click="$router.push({ path: '/dashboard/search-wares', query: { expiresIn: '4h' } })"
                >
                    <template #subtitle>{{ $t("dashboard.entries") }}</template>
                </dashboard-summary-card>
            </v-col>
            <v-col cols="12" sm="6" lg="6" xl="2" class="d-flex flex-column">
                <dashboard-summary-card
                    :title="$t('dashboard.expires1day')"
                    :value="wareExpiring1d"
                    color="red"
                    @click="$router.push({ path: '/dashboard/search-wares', query: { expiresIn: '1d' } })"
                >
                    <template #subtitle>{{ $t("dashboard.entries") }}</template>
                </dashboard-summary-card>
            </v-col>
        </dashboard-stats-row>

        <v-row>
            <v-col cols="12" sm="6" md="4" lg="3">
                <v-btn block @click="$router.push('/dashboard/publish-truck')">+ {{ $t("sidebar.publishTruck") }}</v-btn>
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="3">
                <v-btn block @click="$router.push('/dashboard/publish-ware')">+ {{ $t("sidebar.publishWare") }}</v-btn>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import axios from "axios";
import { showAlertModal, entryExpiresWithin } from "@/utils";
import truckPng from "@/assets/Truck.png";
import warePng from "@/assets/Ware.png";
import DashboardStatsRow from "@/components/dashboard/DashboardStatsRow.vue";
import DashboardSummaryCard from "@/components/dashboard/DashboardSummaryCard.vue";
import DashboardReservationCard from "@/components/dashboard/DashboardReservationCard.vue";

export default {
    name: "HomeView",
    components: { DashboardStatsRow, DashboardSummaryCard, DashboardReservationCard },
    data() {
        return {
            truckPng,
            warePng,
            truckCount: 0,
            truckViews: 0,
            truckExpiring6h: 0,
            truckExpiring1d: 0,
            wareCount: 0,
            wareViews: 0,
            wareExpiring4h: 0,
            wareExpiring1d: 0,
            truckReceived: 0,
            truckContracted: 0,
            wareReceived: 0,
            wareContracted: 0,
        };
    },
    mounted() {
        this.loadDashboard();
    },
    methods: {
        showAlertModal,
        async loadDashboard() {
            await Promise.all([this.fetchTruckAndWareCounts(), this.fetchReservationCounts()]);
        },
        async fetchTruckAndWareCounts() {
            try {
                const [truckResponse, wareResponse] = await Promise.all([
                    axios.get(`${process.env.VUE_APP_API_URL}/truck-requests/`),
                    axios.get(`${process.env.VUE_APP_API_URL}/ware-requests/`),
                ]);

                const trucks = truckResponse.data;
                const wares = wareResponse.data;

                this.truckCount = trucks.length;
                this.wareCount = wares.length;

                this.truckViews = trucks.reduce((sum, t) => sum + (t.view_count || 0), 0);
                this.wareViews = wares.reduce((sum, w) => sum + (w.view_count || 0), 0);

                this.truckExpiring6h = trucks.filter((t) => entryExpiresWithin(t, 6)).length;
                this.truckExpiring1d = trucks.filter((t) => entryExpiresWithin(t, 24)).length;

                this.wareExpiring4h = wares.filter(
                    (w) => !w.has_pending_reservation && entryExpiresWithin(w, 4)
                ).length;
                this.wareExpiring1d = wares.filter(
                    (w) => !w.has_pending_reservation && entryExpiresWithin(w, 24)
                ).length;
            } catch (error) {
                console.error("Failed to fetch truck/ware counts:", error);
                this.showAlertModal(this.$store, "Failed to fetch truck/ware counts", "danger", 12000, error);
            }
        },
        async fetchReservationCounts() {
            try {
                const base = `${process.env.VUE_APP_API_URL}/reservations/`;
                const q = "dashboard_counts=true";
                const [truckC, truckR, wareC, wareR] = await Promise.all([
                    axios.get(`${base}?my=contracted&entry_type=truck&${q}`),
                    axios.get(`${base}?my=received&entry_type=truck&${q}`),
                    axios.get(`${base}?my=contracted&entry_type=ware&${q}`),
                    axios.get(`${base}?my=received&entry_type=ware&${q}`),
                ]);
                this.truckContracted = truckC.data.length;
                this.truckReceived = truckR.data.length;
                this.wareContracted = wareC.data.length;
                this.wareReceived = wareR.data.length;
            } catch (error) {
                console.error("Failed to fetch reservation counts:", error);
                this.showAlertModal(this.$store, "Failed to fetch reservation counts", "danger", 8000, error);
            }
        },
    },
};
</script>
